import React, { useCallback, useState } from "react"
import { Paragraph } from "../../Components/Paragraph"
import Button from "../../Components/Button"
import { MultiStepFlowContainer } from "../../Components/MultiStepFlow"
import { useOutletContextPlanActions } from "../../hooks/useOutletContextPlanActions"
import { useOutletContextPlan } from "../../hooks/useOutletContextPlan"
import { useNavigate } from "react-router-dom"
import { InviteStatusBadge } from "../../Components/InviteStatusBadge"
import { INVITE_STATUS } from "../../constants/inviteStatus"
import Spinner from "../../Components/Spinner"
import Modal from "../../Components/Modal"

/**
 * GuideInviteSent
 *
 * Page for managing guide invitations after a guide has been invited.
 * Allows users to:
 * - View guide invitation status
 * - Resend invite
 * - Revoke invite and choose themselves
 * - Change guide (revoke and invite someone else)
 */
const GuideInviteSent = () => {
  const {
    handleResendGuideInvite,
    handleCancelGuideInvite,
    handleChooseSelfAsGuide,
    error,
    loading,
  } = useOutletContextPlanActions()
  const { guide, pendingGuide, plan } = useOutletContextPlan()
  const [resent, setResent] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState(null)
  const navigate = useNavigate()

  // Use pendingGuide if available (for pending invitations), otherwise use guide (for accepted)
  const displayGuide = pendingGuide || guide

  const handleResend = useCallback(() => {
    if (displayGuide) {
      handleResendGuideInvite(displayGuide.first_name, displayGuide.last_name, displayGuide.email).then(() => setResent(true))
    }
  }, [handleResendGuideInvite, displayGuide])

  const handleCancelAndChooseSelf = useCallback(() => {
    handleCancelGuideInvite().then(() => {
      handleChooseSelfAsGuide().then(() => {
        setConfirmationModal(null)
        navigate("../")
      })
    })
  }, [handleCancelGuideInvite, handleChooseSelfAsGuide, navigate])

  const handleChangeGuide = useCallback(() => {
    handleCancelGuideInvite().then(() => {
      setConfirmationModal(null)
      navigate("../choose-guide")
    })
  }, [handleCancelGuideInvite, navigate])

  const steps = [
    {
      id: "guide-invite-sent",
      encouragement: "🧭",
      encouragementClassName: "text-3xl",
      title: displayGuide?.invitation_status === INVITE_STATUS.ACCEPTED ? "Guide Accepted" : "Guide Invited",
      renderCustomContent: () => {
        return (
          <div className="mx-auto flex max-w-xl flex-col items-center justify-center gap-4 text-center">
            <Paragraph>The Guide for this plan is:</Paragraph>

            <div className="flex w-full flex-col items-center gap-3 p-6">
              <InviteStatusBadge status={displayGuide?.invitation_status} />
              <div className="flex flex-col items-center gap-1">
                <div className="text-lg font-bold">{displayGuide?.first_name} {displayGuide?.last_name}</div>
                <div className="text-gray-600">{displayGuide?.email}</div>
              </div>
            </div>

            {displayGuide?.invitation_status === INVITE_STATUS.ACCEPTED ? (
              <Paragraph>
                The Guide has accepted and can now complete the{" "}
                <strong>
                  <em>Set Up Checklist</em>
                </strong>{" "}
                to help the student and their Mentors make progress.
              </Paragraph>
            ) : (
              <Paragraph>
                The Guide will receive an email with instructions on how to get started. They can
                check their junk mail folder if they are having trouble finding the email.
              </Paragraph>
            )}
          </div>
        )
      },
      fields: [
        {
          fieldType: "content",
          content: (
            <div className="mb-8 mt-4 flex flex-col items-center justify-center gap-4">
              {error && (
                <div className="mb-4 rounded-md bg-red-50 p-4 text-left">
                  {error?.message ?? "Uh-oh. We ran into a problem"}
                </div>
              )}

              {/* Show resend button only if guide hasn't accepted yet */}
              {displayGuide?.invitation_status === INVITE_STATUS.PENDING && (
                <>
                  {resent ? (
                    <div className="text-green-600">Invite email sent successfully!</div>
                  ) : (
                    <Button
                      onClick={handleResend}
                      disabled={resent || loading}
                      variant="secondary-outline"
                      width="w-64"
                      type="button"
                    >
                      {loading ? "Sending..." : "Resend Invite"}
                    </Button>
                  )}
                </>
              )}

              {/* Action buttons - only show if guide hasn't accepted */}
              {displayGuide?.invitation_status !== INVITE_STATUS.ACCEPTED && (
                <div className="flex flex-col gap-3 border-t pt-4">
                  <div className="mb-2 text-sm text-gray-600">Need to make a change?</div>

                  <Button
                    onClick={() => setConfirmationModal("change")}
                    disabled={loading}
                    variant="gray-ghost"
                    width="w-64"
                    type="button"
                  >
                    Change Guide
                  </Button>

                  <Button
                    onClick={() => setConfirmationModal("self")}
                    disabled={loading}
                    variant="gray-ghost"
                    width="w-64"
                    type="button"
                  >
                    I'll Be the Guide
                  </Button>
                </div>
              )}
            </div>
          ),
        },
      ],
    },
  ]

  const handleComplete = useCallback(() => {
    navigate("../")
  }, [navigate])

  return (
    <>
      {loading && <Spinner overlay message="Processing..." />}

      {/* Confirmation Modal for Change Guide */}
      <Modal
        isOpen={confirmationModal === "change"}
        setIsOpen={() => setConfirmationModal(null)}
        width="md:max-w-xl"
      >
        <div className="flex flex-col items-center gap-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Change Guide?</h2>
          <Paragraph className="text-gray-600">
            This will cancel the current guide's invitation and allow you to invite a different
            person. Are you sure you want to continue?
          </Paragraph>
          <div className="flex w-full flex-col gap-3">
            <Button onClick={handleChangeGuide} variant="secondary" width="w-full" disabled={loading}>
              Yes, Change Guide
            </Button>
            <Button
              onClick={() => setConfirmationModal(null)}
              variant="gray-ghost"
              width="w-full"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal for I'll Be the Guide */}
      <Modal
        isOpen={confirmationModal === "self"}
        setIsOpen={() => setConfirmationModal(null)}
        width="md:max-w-xl"
      >
        <div className="flex flex-col items-center gap-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Become the Guide?</h2>
          <Paragraph className="text-gray-600">
            This will cancel the current guide's invitation and you will become the guide for this
            plan. Are you sure you want to continue?
          </Paragraph>
          <div className="flex w-full flex-col gap-3">
            <Button
              onClick={handleCancelAndChooseSelf}
              variant="secondary"
              width="w-full"
              disabled={loading}
            >
              Yes, I'll Be the Guide
            </Button>
            <Button
              onClick={() => setConfirmationModal(null)}
              variant="gray-ghost"
              width="w-full"
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <MultiStepFlowContainer
        steps={steps}
        initialValues={{}}
        stepId="guide-invite-sent"
        fieldWidth="w-64 mx-auto"
        fieldContainerClassName="flex flex-col items-center"
        onBack={() => {
          navigate(`/plan/${plan?.token}/checklist`)
          return true
        }}
        alwaysShowBackButton={true}
        showProgress={false}
        onComplete={handleComplete}
        completeButtonText="Back to Checklist"
        completeButtonProps={{
          width: "w-64",
          beforeIcon: (
            <i className="fa-solid fa-angle-left absolute left-4 top-1/2 -translate-y-1/2 text-3xl"></i>
          ),
        }}
      />
    </>
  )
}

export default GuideInviteSent